import React from "react";
import { motion } from "framer-motion";
import { Check, MessageCircle, ArrowUpRight } from "lucide-react";
import { PRICING, waLink } from "../data/content";
import { fadeUp, staggerContainer, viewportOnce, EYEBROW } from "../lib/motion";

const PriceCard = ({ name, price, includes }) => (
  <motion.div
    variants={fadeUp}
    data-testid={`price-card-${name}`}
    className="group flex flex-col rounded-3xl bg-white border border-graytone-medium/60 p-6 shadow-soft hover:shadow-medium hover:-translate-y-1.5 transition-all duration-400"
  >
    <h4 className="font-serif text-xl text-navy leading-snug min-h-[3.5rem]">{name}</h4>
    <div className="mt-4 mb-4">
      <p className="text-xs uppercase tracking-[0.18em] text-graytone-dark">Mulai dari</p>
      <p className="font-serif text-3xl font-semibold text-gold-dark mt-1">{price}</p>
    </div>
    {includes && includes.length > 0 && (
      <div className="mb-6 rounded-2xl bg-graytone-light p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-navy">Harga sudah termasuk</p>
        <ul className="mt-2.5 space-y-1.5">
          {includes.map((it) => (
            <li key={it} className="flex items-start gap-2 text-xs text-graytone-dark">
              <Check className="h-3.5 w-3.5 text-gold mt-0.5 shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
    <a
      href={waLink(`Halo FYLegal.ID, saya tertarik dengan layanan ${name} (mulai dari ${price}). Mohon informasi lebih lanjut.`)}
      target="_blank"
      rel="noreferrer"
      data-testid={`price-wa-${name}`}
      className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-5 py-3 text-sm font-semibold hover:bg-gold hover:text-navy transition-all duration-300"
    >
      <MessageCircle className="h-4 w-4" /> Hubungi Kami
    </a>
  </motion.div>
);

const ListCard = ({ heading, items }) => (
  <motion.div
    variants={fadeUp}
    data-testid={`list-card-${heading}`}
    className="flex flex-col rounded-3xl bg-navy p-8 md:p-10 shadow-medium"
  >
    <h4 className="font-serif text-2xl md:text-3xl text-white">{heading}</h4>
    <ul className="mt-6 space-y-3 flex-1">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-white/80">
          <Check className="h-5 w-5 text-gold mt-0.5 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
    <a
      href={waLink(`Halo FYLegal.ID, saya ingin bertanya tentang ${heading}. Mohon informasi lebih lanjut.`)}
      target="_blank"
      rel="noreferrer"
      data-testid={`list-wa-${heading}`}
      className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-all duration-300"
    >
      <MessageCircle className="h-4 w-4" /> Hubungi Kami
    </a>
  </motion.div>
);

export const Pricing = () => {
  const priced = PRICING.filter((p) => p.type === "priced");
  const lists = PRICING.filter((p) => p.type === "list");

  return (
    <section id="harga" data-testid="pricing-section" className="relative bg-graytone-light py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-3xl"
        >
          <span className={EYEBROW}>Daftar Biaya & Paket</span>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-tight text-navy leading-[1.05]">
            Layanan & <span className="italic text-gold-dark">Biaya</span>
          </h2>
          <p className="mt-5 text-graytone-dark text-lg">
            Harga jelas sejak awal — hubungi kami langsung via WhatsApp untuk penawaran sesuai kebutuhan Anda.
          </p>
        </motion.div>

        {priced.map((cat) => (
          <div key={cat.id} className="mt-16">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              className="flex items-center gap-4 mb-8"
            >
              <span className="font-serif text-3xl text-gold-dark/50">{cat.tag}</span>
              <h3 className="font-serif text-2xl md:text-3xl text-navy">{cat.heading}</h3>
              <span className="flex-1 h-px bg-graytone-medium" />
            </motion.div>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            >
              {cat.items.map((it) => (
                <PriceCard key={it.name} {...it} />
              ))}
            </motion.div>
          </div>
        ))}

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-16 grid md:grid-cols-2 gap-6"
        >
          {lists.map((cat) => (
            <ListCard key={cat.id} heading={cat.heading} items={cat.items} />
          ))}
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-12 rounded-3xl bg-white border border-graytone-medium/60 p-8 md:p-10 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-soft"
        >
          <p className="font-serif text-2xl text-navy text-center sm:text-left">
            Butuh layanan lain atau penawaran khusus?
          </p>
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="pricing-cta"
            className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-8 py-4 font-semibold hover:bg-gold-light transition-all duration-300 shadow-gold shrink-0"
          >
            Konsultasi Sekarang <ArrowUpRight className="h-5 w-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
